/** True for `#rgb` or `#rrggbb`. */
export function isValidHex(s: string): boolean {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(s);
}

/**
 * Pick a legible foreground (#000000 or #ffffff) for a given background hex,
 * using WCAG relative luminance. Threshold 0.35 is intentional: it keeps the
 * brand orange (#ff5a1f, luminance ≈ 0.29) and blue (#1e7bff, L≈0.23) on
 * WHITE text, matching the existing outbound-bubble look, while flipping
 * medium-light colors like green (#2ecc5a, L≈0.45) to BLACK so they stay
 * AA-legible. A lower 0.179 threshold (the WCAG-standard crossover) would
 * flip orange to black text and regress the current design.
 *
 * Callers should pass a valid hex (validate with `isValidHex`); invalid input
 * falls back to `'#ffffff'` (safe default matching the brand-orange default).
 */
export function readableText(hex: string): '#000000' | '#ffffff' {
  if (!isValidHex(hex)) return '#ffffff';
  const c = hex.replace('#', '');
  const full = c.length === 3 ? c.split('').map((x) => x + x).join('') : c;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const lin = (v: number) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.35 ? '#000000' : '#ffffff';
}
